import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Lock, Globe, Zap, Bot, Clock, Eye,
  Plus, X, CheckCircle, AlertTriangle, RefreshCw,
  Activity, AlertCircle, ChevronRight
} from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { useTranslation } from '../i18n/useTranslation';
import { mockSecurityLogs, mockSites } from '../services/mockData';
import type { SecurityLog, SecurityEventType } from '../types';
import { clsx } from 'clsx';

// ─── Security Score Ring ──────────────────────────────────────────────
function ScoreRing({ score, t }: { score: number; t: (k: any) => string }) {
  const r = 52;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / 100);
  const color = score >= 80 ? '#22c55e' : score >= 50 ? '#3b82f6' : score >= 30 ? '#f59e0b' : '#ef4444';
  const label = score >= 80 ? t('sec.very_safe') : score >= 50 ? t('sec.safe') : score >= 30 ? t('sec.moderate') : t('sec.weak');

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="140" height="140" className="score-ring">
        <circle cx="70" cy="70" r={r} stroke="#e2e8f0" strokeWidth="10" fill="none" />
        <circle
          cx="70" cy="70" r={r}
          stroke={color}
          strokeWidth="10"
          fill="none"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute text-center">
        <div className="text-4xl font-extrabold" style={{ color }}>{score}</div>
        <div className="text-surface-400 text-xs">/ 100</div>
        <div className="text-xs font-bold mt-0.5" style={{ color }}>{label}</div>
      </div>
    </div>
  );
}

// ─── Toggle Switch ────────────────────────────────────────────────────
function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={clsx(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none',
        enabled ? 'bg-telegram' : 'bg-surface-300'
      )}
    >
      <span
        className={clsx(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          enabled ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  );
}

// ─── Event Type Config ────────────────────────────────────────────────
const eventConfig: Record<SecurityEventType, { icon: string; color: string; label: string }> = {
  page_view: { icon: '👁️', color: 'text-blue-600 bg-blue-50', label: 'Lawatan' },
  password_attempt: { icon: '🔑', color: 'text-yellow-600 bg-yellow-50', label: 'Cubaan Password' },
  password_success: { icon: '✅', color: 'text-green-600 bg-green-50', label: 'Password Betul' },
  password_failed: { icon: '❌', color: 'text-red-600 bg-red-50', label: 'Password Salah' },
  rate_limited: { icon: '⏰', color: 'text-orange-600 bg-orange-50', label: 'Rate Limited' },
  ip_blocked: { icon: '🚫', color: 'text-red-600 bg-red-50', label: 'IP Disekat' },
  captcha_failed: { icon: '🤖', color: 'text-red-600 bg-red-50', label: 'CAPTCHA Gagal' },
  captcha_passed: { icon: '✅', color: 'text-green-600 bg-green-50', label: 'CAPTCHA Lulus' },
  bot_detected: { icon: '🤖', color: 'text-yellow-600 bg-yellow-50', label: 'Bot Dikesan' },
  expired_access: { icon: '⏱️', color: 'text-surface-600 bg-surface-50', label: 'Akses Tamat' },
};

// ─── Security Log Row ─────────────────────────────────────────────────
function LogRow({ log, t }: { log: SecurityLog; t: (k: any) => string }) {
  const config = eventConfig[log.eventType];
  return (
    <div className="flex items-center gap-3 py-3 border-b border-surface-100 last:border-0">
      <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0', config.color)}>
        {config.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-surface-900">{config.label}</span>
          {log.blocked && <span className="badge-red text-xs">{t('sec.blocked')}</span>}
        </div>
        <div className="text-xs text-surface-500 truncate">
          {log.ipHash.substring(0, 20)}... • {log.country || 'Unknown'} • {log.userAgent?.substring(0, 40)}
        </div>
        {log.reason && <div className="text-xs text-red-600 mt-0.5">{log.reason}</div>}
      </div>
      <div className="text-xs text-surface-400 flex-shrink-0">
        {new Date(log.timestamp).toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────
export default function SecurityPage() {
  const { mySites } = useAppStore();
  const { t } = useTranslation();
  const site = mySites[0] || mockSites[0];

  const [security, setSecurity] = useState(site.security);
  const [newIp, setNewIp] = useState('');
  const [ipMode, setIpMode] = useState<'whitelist' | 'blacklist'>('blacklist');
  const [newPassword, setNewPassword] = useState('');
  const [rateLimit, setRateLimit] = useState(security.rateLimitPerMinute);
  const [expiryDate, setExpiryDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const score = security.securityScore;

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((r) => setTimeout(r, 1000));
    setIsSaving(false);
  };

  const addIp = () => {
    if (!newIp.trim()) return;
    const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!cidrRegex.test(newIp.trim())) return;
    if (ipMode === 'blacklist') {
      setSecurity({ ...security, ipBlacklist: [...security.ipBlacklist, newIp.trim()] });
    } else {
      setSecurity({ ...security, ipWhitelist: [...security.ipWhitelist, newIp.trim()] });
    }
    setNewIp('');
  };

  const removeIp = (ip: string, list: 'whitelist' | 'blacklist') => {
    if (list === 'blacklist') {
      setSecurity({ ...security, ipBlacklist: security.ipBlacklist.filter((i) => i !== ip) });
    } else {
      setSecurity({ ...security, ipWhitelist: security.ipWhitelist.filter((i) => i !== ip) });
    }
  };

  const sections = [
    { id: 'password', icon: Lock, title: t('sec.password'), desc: t('sec.password_desc'), points: 15, active: security.passwordEnabled },
    { id: 'ratelimit', icon: Zap, title: t('sec.ratelimit'), desc: t('sec.ratelimit_desc'), points: 15, active: security.rateLimitEnabled },
    { id: 'ipcontrol', icon: Globe, title: t('sec.ipcontrol'), desc: t('sec.ipcontrol_desc'), points: 10, active: security.ipWhitelistEnabled || security.ipBlacklistEnabled },
    { id: 'captcha', icon: Bot, title: t('sec.captcha'), desc: t('sec.captcha_desc'), points: 10, active: security.captchaEnabled },
    { id: 'expiry', icon: Clock, title: t('sec.expiry'), desc: t('sec.expiry_desc'), points: 5, active: security.expiryEnabled },
  ];

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <div className="bg-white border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <Shield size={28} className="text-telegram" />
                <h1 className="text-3xl font-extrabold text-surface-900">{t('sec.title')}</h1>
              </div>
              <p className="text-surface-500">{site.siteName} • {site.publicUrl || 'Draft'}</p>
            </div>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary btn-md"
            >
              {isSaving ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle size={16} />}
              {t('sec.save')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">

          {/* ─── Left: Score + Overview ─────────────────────────── */}
          <div className="space-y-6">
            {/* Score Card */}
            <div className="card p-6 text-center">
              <h3 className="font-bold text-surface-900 mb-6">{t('sec.score_title')}</h3>
              <ScoreRing score={score} t={t} />
              <div className="mt-6 space-y-2">
                {[
                  { label: t('sec.https'), active: true, points: 20 },
                  { label: t('sec.csp'), active: true, points: 20 },
                  ...sections.map((s) => ({ label: s.title, active: s.active, points: s.points })),
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {item.active
                        ? <CheckCircle size={14} className="text-green-500" />
                        : <X size={14} className="text-surface-300" />}
                      <span className={item.active ? 'text-surface-700' : 'text-surface-400'}>{item.label}</span>
                    </div>
                    <span className={clsx('text-xs font-bold', item.active ? 'text-green-600' : 'text-surface-400')}>
                      +{item.points}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="card p-5">
              <h3 className="font-bold text-surface-900 mb-4 text-sm flex items-center gap-2">
                <Activity size={16} className="text-telegram" />
                {t('sec.activity')}
              </h3>
              <div className="space-y-3">
                {[
                  { label: t('sec.visits'), value: '298', icon: Eye, color: 'text-blue-600' },
                  { label: t('sec.ip_blocked'), value: '3', icon: Globe, color: 'text-red-600' },
                  { label: t('sec.rate_limited'), value: '12', icon: Zap, color: 'text-yellow-600' },
                  { label: t('sec.bots_detected'), value: '7', icon: Bot, color: 'text-purple-600' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-surface-600">
                      <stat.icon size={14} className={stat.color} />
                      {stat.label}
                    </div>
                    <span className="font-bold text-surface-900">{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ─── Right: Settings + Logs ──────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Security Sections */}
            {sections.map((section) => (
              <div key={section.id} className="card">
                <button
                  onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-surface-50 transition-colors rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className={clsx(
                      'w-10 h-10 rounded-xl flex items-center justify-center',
                      section.active ? 'bg-telegram/10' : 'bg-surface-100'
                    )}>
                      <section.icon size={20} className={section.active ? 'text-telegram' : 'text-surface-400'} />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-surface-900 flex items-center gap-2">
                        {section.title}
                        <span className="badge bg-surface-100 text-surface-500 text-xs">+{section.points} pts</span>
                      </div>
                      <div className="text-surface-500 text-sm">{section.desc}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Toggle
                      enabled={
                        section.id === 'password' ? security.passwordEnabled :
                        section.id === 'ratelimit' ? security.rateLimitEnabled :
                        section.id === 'ipcontrol' ? (security.ipWhitelistEnabled || security.ipBlacklistEnabled) :
                        section.id === 'captcha' ? security.captchaEnabled :
                        section.id === 'expiry' ? security.expiryEnabled : false
                      }
                      onChange={(v) => {
                        if (section.id === 'password') setSecurity({ ...security, passwordEnabled: v });
                        else if (section.id === 'ratelimit') setSecurity({ ...security, rateLimitEnabled: v });
                        else if (section.id === 'ipcontrol') setSecurity({ ...security, ipBlacklistEnabled: v, ipWhitelistEnabled: false });
                        else if (section.id === 'captcha') setSecurity({ ...security, captchaEnabled: v });
                        else if (section.id === 'expiry') setSecurity({ ...security, expiryEnabled: v });
                      }}
                    />
                    <ChevronRight size={16} className={clsx(
                      'text-surface-400 transition-transform',
                      activeSection === section.id && 'rotate-90'
                    )} />
                  </div>
                </button>

                {/* Expanded Content */}
                {activeSection === section.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="border-t border-surface-100 p-5"
                  >
                    {/* Password Section */}
                    {section.id === 'password' && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-surface-700 mb-1.5">{t('sec.new_password')}</label>
                          <div className="flex gap-2">
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder={t('sec.password_ph')}
                              className="input flex-1"
                            />
                            <button className="btn-primary btn-md">{t('sec.set_password')}</button>
                          </div>
                          <p className="text-xs text-surface-400 mt-1.5 flex items-center gap-1">
                            <Shield size={10} />
                            {t('sec.password_note')}
                          </p>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700 flex items-start gap-2">
                          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                          {t('sec.password_warning')}
                        </div>
                      </div>
                    )}

                    {/* Rate Limit Section */}
                    {section.id === 'ratelimit' && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-surface-700 mb-3">
                            {t('sec.rate_label')} <span className="font-bold text-telegram">{rateLimit}</span>
                          </label>
                          <input
                            type="range"
                            min="10"
                            max="1000"
                            step="10"
                            value={rateLimit}
                            onChange={(e) => setRateLimit(parseInt(e.target.value))}
                            className="w-full h-2 bg-surface-200 rounded-lg appearance-none cursor-pointer accent-telegram"
                          />
                          <div className="flex justify-between text-xs text-surface-400 mt-1">
                            <span>10 {t('sec.strict')}</span>
                            <span>100 {t('sec.standard')}</span>
                            <span>1000 {t('sec.loose')}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {[30, 60, 100].map((v) => (
                            <button
                              key={v}
                              onClick={() => setRateLimit(v)}
                              className={clsx(
                                'py-2 rounded-lg text-sm font-medium border transition-all',
                                rateLimit === v
                                  ? 'border-telegram bg-telegram/10 text-telegram'
                                  : 'border-surface-200 text-surface-600 hover:border-telegram/50'
                              )}
                            >
                              {v}/min
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* IP Control Section */}
                    {section.id === 'ipcontrol' && (
                      <div className="space-y-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setIpMode('blacklist')}
                            className={clsx('btn-sm flex-1 rounded-lg border font-medium text-sm transition-all',
                              ipMode === 'blacklist' ? 'border-red-400 bg-red-50 text-red-700' : 'border-surface-200 text-surface-600'
                            )}
                          >
                            {t('sec.blocklist')}
                          </button>
                          <button
                            onClick={() => setIpMode('whitelist')}
                            className={clsx('btn-sm flex-1 rounded-lg border font-medium text-sm transition-all',
                              ipMode === 'whitelist' ? 'border-green-400 bg-green-50 text-green-700' : 'border-surface-200 text-surface-600'
                            )}
                          >
                            {t('sec.allowlist')}
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newIp}
                            onChange={(e) => setNewIp(e.target.value)}
                            placeholder={t('sec.ip_ph')}
                            className="input flex-1 font-mono text-sm"
                            onKeyDown={(e) => e.key === 'Enter' && addIp()}
                          />
                          <button onClick={addIp} className="btn-primary btn-md">
                            <Plus size={16} />
                          </button>
                        </div>
                        <div className="space-y-2">
                          {(ipMode === 'blacklist' ? security.ipBlacklist : security.ipWhitelist).map((ip) => (
                            <div key={ip} className="flex items-center justify-between bg-surface-50 rounded-lg px-3 py-2">
                              <span className="font-mono text-sm text-surface-700">{ip}</span>
                              <button onClick={() => removeIp(ip, ipMode)} className="text-red-500 hover:text-red-700">
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                          {(ipMode === 'blacklist' ? security.ipBlacklist : security.ipWhitelist).length === 0 && (
                            <div className="text-center py-4 text-surface-400 text-sm">
                              {t('sec.no_ip')} {ipMode === 'blacklist' ? 'blocklist' : 'allowlist'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* CAPTCHA Section */}
                    {section.id === 'captcha' && (
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                          <div className="font-bold mb-1">{t('sec.turnstile_title')}</div>
                          <p>{t('sec.turnstile_desc')}</p>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-surface-600">
                          <CheckCircle size={16} className="text-green-500" />
                          {t('sec.turnstile_ok')}
                        </div>
                      </div>
                    )}

                    {/* Expiry Section */}
                    {section.id === 'expiry' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-surface-700 mb-1.5">{t('sec.expiry_label')}</label>
                          <input
                            type="datetime-local"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            className="input"
                          />
                        </div>
                        <div className="bg-surface-50 rounded-lg p-3 text-sm text-surface-600 flex items-start gap-2">
                          <Clock size={14} className="mt-0.5 flex-shrink-0" />
                          {t('sec.expiry_note')}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ))}

            {/* Security Logs */}
            <div className="card">
              <div className="flex items-center justify-between p-5 border-b border-surface-100">
                <h3 className="font-bold text-surface-900 flex items-center gap-2">
                  <Activity size={18} className="text-telegram" />
                  {t('sec.logs')}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="badge-red text-xs">3 {t('sec.blocked_today')}</span>
                  <button className="btn-outline btn-sm">{t('sec.view_all')}</button>
                </div>
              </div>
              <div className="p-5">
                {mockSecurityLogs.map((log) => (
                  <LogRow key={log.logId} log={log} t={t} />
                ))}
              </div>
            </div>

            {/* Abuse Detector */}
            <div className="card p-5 border-orange-200 bg-orange-50">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-orange-800 mb-1">{t('sec.abuse_title')}</div>
                  <p className="text-orange-700 text-sm mb-3">{t('sec.abuse_sub')}</p>
                  <div className="space-y-1 text-sm text-orange-700">
                    {[
                      '> 500 req dalam 10 minit dari satu IP',
                      'Cubaan akses /.env, /admin, /wp-login',
                      'Kadar ralat 4xx/5xx > 50% dari satu IP',
                      'Corak scraping yang mencurigakan',
                    ].map((rule) => (
                      <div key={rule} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                        {rule}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs text-orange-600">
                    {t('sec.abuse_action')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
